import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import clsx from 'clsx';
import { toast } from 'sonner';
import api from '../services/api';
import PageTransition from '../components/ui/PageTransition';
import EmptyState from '../components/ui/EmptyState';

// ─── Animation variants ────────────────────────────────────────────────────

const listContainer = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.05 },
  },
};

const listItem = {
  hidden: { opacity: 0, y: 8 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.18, ease: 'easeOut' } },
  exit:   { opacity: 0, x: 12, transition: { duration: 0.15 } },
};

// ─── Skeleton ──────────────────────────────────────────────────────────────

const NotificationSkeleton = () => (
  <div className="flex items-start gap-4 px-5 py-4">
    <div className="w-10 h-10 rounded-full skeleton flex-shrink-0" />
    <div className="flex-1 space-y-2">
      <div className="h-3.5 w-48 skeleton rounded-md" />
      <div className="h-3 w-full skeleton rounded-md" />
      <div className="h-3 w-3/4 skeleton rounded-md" />
      <div className="h-3 w-20 skeleton rounded-md" />
    </div>
    <div className="flex flex-col items-end gap-2 flex-shrink-0">
      <div className="w-5 h-5 skeleton rounded" />
      <div className="w-5 h-5 skeleton rounded" />
    </div>
  </div>
);

const NotificationsPageSkeleton = () => (
  <div className="cliqq-container min-h-screen">
    <div className="max-w-3xl mx-auto px-4 py-6">
      <div className="bg-white rounded-2xl shadow-soft-sm border border-surface-200 overflow-hidden">
        {/* Header skeleton */}
        <div className="px-6 py-5 border-b border-surface-200 space-y-4">
          <div className="flex items-center justify-between">
            <div className="h-7 w-44 skeleton rounded-lg" />
            <div className="flex items-center gap-3">
              <div className="h-5 w-28 skeleton rounded-lg" />
              <div className="h-8 w-8 skeleton rounded-lg" />
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-9 w-24 skeleton rounded-xl" />
            ))}
          </div>
        </div>
        {/* Items skeleton */}
        <div className="divide-y divide-surface-100">
          {[...Array(5)].map((_, i) => (
            <NotificationSkeleton key={i} />
          ))}
        </div>
      </div>
    </div>
  </div>
);

// ─── Notification icon ─────────────────────────────────────────────────────

const ICON_CONFIG = {
  system:      { bg: 'bg-primary-100',  text: 'text-primary-600'  },
  maintenance: { bg: 'bg-warning-50',   text: 'text-warning-600'  },
  feature:     { bg: 'bg-info-50',      text: 'text-info-600'     },
  friend_request: { bg: 'bg-success-50', text: 'text-success-600' },
  social:      { bg: 'bg-accent-100',   text: 'text-accent-600'   },
  comment:     { bg: 'bg-accent-100',   text: 'text-accent-600'   },
  followers:   { bg: 'bg-accent-100',   text: 'text-accent-600'   },
  tagged:      { bg: 'bg-accent-100',   text: 'text-accent-600'   },
};

const NotificationIcon = ({ type }) => {
  const cfg = ICON_CONFIG[type] || { bg: 'bg-surface-100', text: 'text-surface-500' };

  const iconPath = (() => {
    switch (type) {
      case 'system':
      case 'maintenance':
      case 'feature':
        return (
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        );
      case 'friend_request':
        return (
          <path d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 7a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V7z" />
        );
      case 'social':
      case 'comment':
      case 'followers':
      case 'tagged':
        return (
          <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
        );
      default:
        return (
          <path fillRule="evenodd" d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zm0 16a2 2 0 01-2-2h4a2 2 0 01-2 2z" clipRule="evenodd" />
        );
    }
  })();

  return (
    <div
      className={clsx(
        'w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0',
        cfg.bg,
        cfg.text
      )}
      aria-hidden="true"
    >
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        {iconPath}
      </svg>
    </div>
  );
};

// ─── Tab button ────────────────────────────────────────────────────────────

const TabButton = ({ label, count, icon, isActive, onClick }) => (
  <button
    onClick={onClick}
    aria-pressed={isActive}
    className={clsx(
      'flex items-center gap-2 px-3.5 py-2 rounded-xl text-body-sm font-medium transition-all duration-200 whitespace-nowrap',
      isActive
        ? 'bg-primary-500 text-white shadow-colored'
        : 'bg-surface-100 text-surface-600 hover:bg-surface-200 hover:text-surface-800'
    )}
  >
    <span className="w-4 h-4" aria-hidden="true">{icon}</span>
    {label}
    {count > 0 && (
      <span
        className={clsx(
          'text-caption font-semibold px-1.5 py-0.5 rounded-full',
          isActive ? 'bg-white/25 text-white' : 'bg-surface-200 text-surface-600'
        )}
      >
        {count}
      </span>
    )}
  </button>
);

// ─── Single notification row ───────────────────────────────────────────────

const NotificationRow = ({ notification, onMarkRead, onDismiss }) => {
  const formatTimestamp = (ts) => {
    const diff = Date.now() - ts;
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(mins / 60);
    const days = Math.floor(hours / 24);
    if (days > 0)  return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (mins > 0)  return `${mins}m ago`;
    return 'Just now';
  };

  return (
    <motion.div
      variants={listItem}
      layout
      className={clsx(
        'flex items-start gap-4 px-5 py-4 transition-colors group',
        notification.isRead ? 'bg-white hover:bg-surface-50' : 'bg-primary-50 hover:bg-primary-50/70'
      )}
    >
      <NotificationIcon type={notification.type} />

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-body-sm font-semibold text-surface-900">
                {notification.title}
              </h3>
              {!notification.isRead && (
                <span className="w-2 h-2 bg-primary-500 rounded-full flex-shrink-0" aria-label="Unread" />
              )}
            </div>
            <p className="text-body-sm text-surface-500 mt-0.5 line-clamp-2">
              {notification.message}
            </p>
            <p className="text-caption text-surface-400 mt-1.5">
              {formatTimestamp(notification.timestamp)}
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
            {!notification.isRead && (
              <button
                onClick={() => onMarkRead([notification.id])}
                title="Mark as read"
                aria-label="Mark as read"
                className="p-1.5 rounded-lg text-surface-400 hover:text-primary-600 hover:bg-primary-50 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </button>
            )}
            <button
              onClick={() => onDismiss(notification.id)}
              title="Dismiss"
              aria-label="Dismiss notification"
              className="p-1.5 rounded-lg text-surface-400 hover:text-error-500 hover:bg-error-50 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// ─── Server → UI mapping ────────────────────────────────────────────────────

// Group server notification types into the page's tab categories.
const TYPE_TO_CATEGORY = {
  system_announcement: 'General',
  account_verified: 'General',
  friend_request: 'Requests',
  group_invite: 'Requests',
  event_invite: 'Requests',
  friend_accepted: 'Social',
  personal_cliqq_added: 'Social',
  group_joined: 'Social',
  group_left: 'Social',
  group_event_added: 'Social',
  share_received: 'Social',
  share_clicked: 'Social',
  message: 'Social',
  message_share: 'Social',
  event_update: 'Reminders',
  event_cancelled: 'Reminders',
  event_reminder: 'Reminders',
  event_starting: 'Reminders',
  event_joined: 'Reminders',
  event_left: 'Reminders',
  event_comment: 'Social',
  promotion_new: 'Business',
  promotion_expiring: 'Business',
};

const mapNotification = (n) => ({
  id: n.id ?? n.ID,
  type: n.type,
  title: n.title,
  message: n.message,
  timestamp: n.created_at ? new Date(n.created_at).getTime() : Date.now(),
  isRead: n.is_read ?? n.isRead ?? false,
  category: TYPE_TO_CATEGORY[n.type] || 'General',
});

// ─── Tab icon helpers ──────────────────────────────────────────────────────

const TABS = [
  {
    key: 'All',
    label: 'All',
    icon: (
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-4 h-4">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
      </svg>
    ),
  },
  {
    key: 'General',
    label: 'General',
    icon: (
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-4 h-4">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    key: 'Social',
    label: 'Social',
    icon: (
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-4 h-4">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    ),
  },
  {
    key: 'Requests',
    label: 'Requests',
    icon: (
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-4 h-4">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
      </svg>
    ),
  },
  {
    key: 'Reminders',
    label: 'Reminders',
    icon: (
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-4 h-4">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    key: 'Business',
    label: 'Business',
    icon: (
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-4 h-4">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
    ),
  },
];

// ─── Main component ────────────────────────────────────────────────────────

const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/notifications');
      const data = response.data?.data || response.data || [];
      const arr = Array.isArray(data) ? data.map(mapNotification) : [];
      setNotifications(arr);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const markAsRead = async (ids) => {
    if (!Array.isArray(ids) || ids.length === 0) return;
    try {
      await Promise.all(ids.map((id) => api.put(`/api/notifications/${id}/read`)));
      setNotifications((prev) =>
        prev.map((n) => (ids.includes(n.id) ? { ...n, isRead: true } : n))
      );
    } catch (err) {
      console.error('Failed to mark notifications as read:', err);
      toast.error('Could not mark notification as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.put('/api/notifications/read-all');
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      toast.success('All notifications marked as read');
    } catch (err) {
      console.error('Failed to mark all as read:', err);
      toast.error('Could not mark all as read');
    }
  };

  const dismissNotification = async (id) => {
    if (!id) return;
    try {
      await api.delete(`/api/notifications/${id}`);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    } catch (err) {
      console.error('Failed to dismiss notification:', err);
      toast.error('Could not dismiss notification');
    }
  };

  const getFiltered = () => {
    if (filter === 'All') return notifications;
    return notifications.filter((n) => n.category === filter);
  };

  const getCategoryCount = (cat) =>
    cat === 'All'
      ? notifications.length
      : notifications.filter((n) => n.category === cat).length;

  const unreadCount  = notifications.filter((n) => !n.isRead).length;
  const filtered     = getFiltered();

  if (loading) return <NotificationsPageSkeleton />;

  return (
    <PageTransition className="cliqq-container min-h-screen">
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="bg-white rounded-2xl shadow-soft-sm border border-surface-200 overflow-hidden">

          {/* ── Header ── */}
          <div className="px-6 py-5 border-b border-surface-200">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h1 className="text-h2 text-surface-900">Notifications</h1>
                {unreadCount > 0 && (
                  <p className="text-body-sm text-surface-500 mt-0.5">
                    {unreadCount} unread
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <motion.button
                    whileTap={{ scale: 0.97 }}
                    onClick={markAllAsRead}
                    className="flex items-center gap-2 px-3.5 py-2 bg-primary-50 hover:bg-primary-100 text-primary-700 text-body-sm font-semibold rounded-xl transition-colors"
                    aria-label="Mark all notifications as read"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                    </svg>
                    <span className="hidden sm:inline">Mark all as read</span>
                  </motion.button>
                )}
                {/* Had no onClick at all, so the gear was decorative. The
                    notification preferences UI lives in Settings. */}
                <Link
                  to="/settings"
                  className="p-2 rounded-xl text-surface-400 hover:text-surface-700 hover:bg-surface-100 transition-colors"
                  aria-label="Notification settings"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </Link>
              </div>
            </div>

            {/* Category tabs */}
            <div className="flex gap-2 flex-wrap">
              {TABS.map(({ key, label, icon }) => (
                <TabButton
                  key={key}
                  label={label}
                  count={getCategoryCount(key)}
                  icon={icon}
                  isActive={filter === key}
                  onClick={() => setFilter(key)}
                />
              ))}
            </div>
          </div>

          {/* ── Notification list ── */}
          <div
            className="divide-y divide-surface-100"
            role="list"
            aria-label="Notifications"
          >
            <AnimatePresence mode="wait">
              {filtered.length > 0 ? (
                <motion.div
                  key={filter}
                  variants={listContainer}
                  initial="hidden"
                  animate="show"
                >
                  {filtered.map((notification) => (
                    <NotificationRow
                      key={notification.id}
                      notification={notification}
                      onMarkRead={markAsRead}
                      onDismiss={dismissNotification}
                    />
                  ))}
                </motion.div>
              ) : (
                <motion.div
                  key="empty"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.2 }}
                >
                  <EmptyState
                    icon={
                      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                      </svg>
                    }
                    title="No notifications"
                    description={
                      filter === 'All'
                        ? "You're all caught up. Nothing new right now."
                        : `No ${filter.toLowerCase()} notifications to show.`
                    }
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

        </div>
      </div>
    </PageTransition>
  );
};

export default NotificationsPage;
